import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCaretDown,
  faCaretUp,
  faSearch,
  faFilterList,
  faBoxOpen,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAgreementsForUser } from "../../api/Agreement"; // Replace with your actual API functions
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import InputField from "../../components/InputField";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Switch from "../../components/Switch";
import Dropdown from "../../components/Dropdown";
import Breadcrumbs from "../../components/Breadcrumbs";

const AgreementsPage = () => {
  const { getAccessToken, user } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [agreementsLoaded, setAgreementsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const [rowsPerTable, setRowsPerTable] = useState(10);
  const [page, setPage] = useState(1);
  const numberOfRows = filteredAgreements.length;
  const numberOfPages = Math.ceil(numberOfRows / rowsPerTable);

  const paginatedAgreements = filteredAgreements.slice(
    (page - 1) * rowsPerTable, page * rowsPerTable
  );

  const startRow = (page - 1) * rowsPerTable + 1;
  const endRow = Math.min(page * rowsPerTable, numberOfRows);

  useEffect(() => {
    handleGetAgreements();
  }, [getAccessToken, user]);

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = agreements;
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = agreements.filter(
          (agreement) =>
            agreement.agreementPublicId?.toLowerCase().includes(searchLower) ||
            agreement.name?.toLowerCase().includes(searchLower) ||
            agreement.status?.toLowerCase().includes(searchLower)
        );
      }
      const sorted = [...filtered].sort((a, b) => {
        const aValue = a[sortColumn]?.toString().toLowerCase();
        const bValue = b[sortColumn]?.toString().toLowerCase();
        return sortOrder === "asc"
          ? aValue > bValue
            ? 1
            : -1
          : aValue < bValue
          ? 1
          : -1;
      });
      setFilteredAgreements(sorted);
    };

    applyFilterAndSort();
  }, [search, agreements, sortColumn, sortOrder]);

  const handleGetAgreements = async () => {
    try {
      const accessToken = await getAccessToken();
      const userId = user.homeAccountId; // Use the authenticated user's ID
      const newAgreements = await getAgreementsForUser(accessToken);
      setAgreements(newAgreements);
      setFilteredAgreements(newAgreements);
      setAgreementsLoaded(true);
    } catch (error) {
      console.error("Failed to fetch agreements:", error);
      setAgreementsLoaded(true);
    }
  };

  /*   const handleDeleteAgreement = async (agreementId) => {
    const toastId = toast.loading("Deleting agreement...");
    try {
      const accessToken = await getAccessToken();
      await deleteAgreement(agreementId, accessToken);
      const newAgreements = await getAllAgreementsForUsers(accessToken);
      setAgreements(newAgreements);
      toast.success("Agreement deleted", { id: toastId });
    } catch (error) {
      toast.error("Failed to delete agreement", { id: toastId });
      console.error(error);
    }
  }; */

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? faCaretDown : faCaretUp;
    }
    return null;
  };

  const [selectedAgreements, setSelectedAgreements] = useState([]);

  const handleSelectAll = (checked) => {
    if (filteredAgreements.length === 0) return; // Prevent selection when list is empty
    setSelectedAgreements(
      checked
        ? filteredAgreements.map((agreement) => agreement.agreementPublicId)
        : []
    );
  };

  const handleSelectAgreement = (agreementPublicId) => {
    setSelectedAgreements((prevSelectedAgreements) =>
      prevSelectedAgreements.includes(agreementPublicId)
        ? prevSelectedAgreements.filter((id) => id !== agreementPublicId)
        : [...prevSelectedAgreements, agreementPublicId]
    );
  };


  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage
                title={"Agreements"}
                loading={!agreementsLoaded}
              />
            </div>

            <div
              className={`${!agreementsLoaded ? "loading" : ""} page-content`}
            >
              <Breadcrumbs />
              
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">
                  Agreements
                </h2>
              </div>

              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"name, status"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <table className="table-default w-full">
                    <thead>
                      <tr>
                        <th>
                          <Switch
                            type="checkbox"
                            checked={
                              filteredAgreements.length > 0 &&
                              selectedAgreements.length ===
                                filteredAgreements.length
                            }
                            onChange={handleSelectAll}
                            disabled={filteredAgreements.length === 0} // Disable when no agreements found
                          />
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          ID
                          {getSortIcon("name") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("name")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Comment
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Created by
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Created date
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Name
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Status
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Updated by
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-left cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Updated date
                          {getSortIcon("status") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("status")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAgreements.length > 0 ? (
                        paginatedAgreements.map((agreement, index) => (
                          <tr key={index}>
                            <td>
                              <Switch
                                type="checkbox"
                                checked={selectedAgreements.includes(
                                  agreement.agreementPublicId
                                )}
                                onChange={() =>
                                  handleSelectAgreement(
                                    agreement.agreementPublicId
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.agreementPublicId || ""}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.comment || ""}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.createdBy || ""}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.createdDateTimeUTC || ""}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.name || ""}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.status || "N/A"}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.updatedBy || "N/A"}
                              </Link>
                            </td>
                            <td>
                              <Link
                                to={`/agreements/${agreement.agreementPublicId}/edit`}
                                className="cursor-pointer"
                              >
                                {agreement.updatedDateTimeUTC || "N/A"}
                              </Link>
                            </td>
                            <td className="flex justify-end">
                              <CustomDropdown
                                isRight={true}
                                ContentComponent={() => (
                                  <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <Link
                                      to={`/agreements/${agreement.agreementPublicId}/edit`}
                                      className="block px-4 py-2 text-sm text-black hover:bg-gray-200 w-full text-left rounded-lg"
                                    >
                                      <FontAwesomeIcon
                                        icon={faPenToSquare}
                                        className="mr-4"
                                      />
                                      Edit
                                    </Link>
                                  </div>
                                )}
                              >
                                <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                  <FontAwesomeIcon icon={faEllipsis} />
                                </div>
                              </CustomDropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">
                            <div className="flex flex-col items-center">
                              <FontAwesomeIcon
                                icon={faBoxOpen}
                                className="text-4xl text-gray-300 mt-6 pb-4"
                              />
                              <span>No available information</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="flex gap-4 justify-end items-center text-black pt-4">
                    <p className="text-sm">Rows per page</p>
                    <div className="w-[72px]">
                      <Dropdown
                        className="mb-1"
                        size="small"
                        options={["10", "30", "50", "70", "100"]}
                        onSelect={(rows) => setRowsPerTable(parseInt(rows, 10))}
                        selectedOptions={[rowsPerTable]}
                      />
                    </div>
                    <p className="text-sm">{`${startRow}-${endRow} of ${numberOfRows}`}</p>
                    <FontAwesomeIcon
                      className="cursor-pointer"
                      icon={faChevronLeft}
                      onClick={() => {
                        if (page > 1) setPage(page - 1);
                      }}
                    />
                    <FontAwesomeIcon
                      className="cursor-pointer"
                      icon={faChevronRight}
                      onClick={() => {
                        if (page < numberOfPages) setPage(page + 1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default AgreementsPage;
